import { ReactElement } from 'react'

import { Button } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { formatDate } from 'utils/dates'

import { getCurrentUser, getDateFormat } from 'store/currentUser/selectors'

const DEFAULT_SUBJECT = 'Hello from'

const formatEmailBody = (body: string): string[] => {
  if (body?.includes('\n')) {
    return body?.split('\n')
  }

  return [body]
}

const PreviewContainer = styled.div`
  width: 690px;
  border: 1px solid var(--color-primary-200);
  border-radius: 10px;
  margin: 16px auto;
  padding: 16px 22px;
`

const EmailSubject = styled.h2`
  font-family: var(--font-family-alpha);
  font-weight: var(--font-weight-regular);
  font-size: 20px;
  line-height: var(--line-height-32);
  letter-spacing: 1.2px;
`

const OrderInfoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 20px;
  margin-top: 18px;
`

const OrderInfoCard = styled.div`
  width: 100%;
  border: 1px solid var(--color-primary-200);
  border-radius: 10px;
`

const OrderInfoHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  height: 42px;
  padding-left: 16px;
  background: var(--color-secondary-100);
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
  border-bottom: 1px solid var(--color-primary-200);
  font-family: var(--font-family-alpha);
  font-weight: var(--font-weight-medium);
  font-size: 14px;
  line-height: var(--line-height-16);
  letter-spacing: 1px;
`

const OrderInfoContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const InfoRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InfoRowTitle = styled.p`
  font-family: var(--font-family-alpha);
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-16);
  letter-spacing: 0.6px;
  margin: 0;
`

const InfoRowContent = styled.p`
  font-family: var(--font-family-alpha);
  font-size: 14px;
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-16);
  letter-spacing: 1px;
  margin: 0;
  width: 300px;
  a {
    color: #041dfe;
    text-decoration: none;
    word-wrap: break-word;
  }
`

const EmailBodyText = styled.p`
  font-family: var(--font-family-alpha);
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-16);
  letter-spacing: 0.6px;
  a {
    color: #041dfe;
    text-decoration: none;
  }
`

const PayNowButton = styled(Button)`
  width: 250px;
`

type OrderInfoRowProps = {
  title: string
  content: string | ReactElement
}

const OrderInfoRow = ({
  title = 'Payment Name',
  content = 'Deposit',
}: OrderInfoRowProps) => {
  return (
    <InfoRowContainer>
      <InfoRowTitle>{title}</InfoRowTitle>
      <InfoRowContent>{content}</InfoRowContent>
    </InfoRowContainer>
  )
}

type EmailPreviewProps = {
  subject?: string
  body?: string
  brandName?: string
  paymentName?: string
  paymentAmount?: string
  dueDate?: string
  attachment?: {
    url: string
    fileName: string
  }
  billingAddress?: string
  retailerName?: string
  retailerEmail?: string
}

const EmailPreview = ({
  subject,
  body,
  brandName = '',
  paymentName,
  paymentAmount,
  dueDate,
  attachment,
  billingAddress,
  retailerName,
  retailerEmail,
}: EmailPreviewProps) => {
  const currentUser = useSelector(getCurrentUser) //TODO: convert this into a prop
  const userDateFormat = useSelector(getDateFormat) as string
  const username = currentUser?.name || currentUser?.email || '--'
  const userEmail = currentUser?.email || '--'

  return (
    <PreviewContainer>
      <EmailSubject>
        {subject || `${DEFAULT_SUBJECT} ${brandName}`}
      </EmailSubject>
      <OrderInfoContainer>
        <OrderInfoCard>
          <OrderInfoHeader>Request Details</OrderInfoHeader>
          <OrderInfoContent>
            {paymentName && (
              <OrderInfoRow title="Payment Name" content={paymentName} />
            )}
            {paymentAmount && (
              <OrderInfoRow title="Payment Amount" content={paymentAmount} />
            )}
            {dueDate && (
              <OrderInfoRow
                title="Due Date"
                content={formatDate(dueDate, userDateFormat?.toUpperCase())}
              />
            )}
            {attachment && (
              <OrderInfoRow
                title="Invoice"
                content={
                  <a
                    target="_blank"
                    href={`${attachment.url}`}
                    rel="noreferrer"
                  >
                    {attachment.fileName}
                  </a>
                }
              />
            )}
          </OrderInfoContent>
        </OrderInfoCard>
        <OrderInfoCard>
          <OrderInfoHeader>Billing Info</OrderInfoHeader>
          <OrderInfoContent>
            {billingAddress && (
              <OrderInfoRow title="Billing Address" content={billingAddress} />
            )}
            {retailerName && (
              <OrderInfoRow title="Retailer" content={retailerName} />
            )}
            {retailerEmail && (
              <OrderInfoRow
                title="Email"
                content={
                  <a href={`mailto:${retailerEmail}`}>{retailerEmail}</a>
                }
              />
            )}
          </OrderInfoContent>
        </OrderInfoCard>
      </OrderInfoContainer>
      <div style={{ marginTop: '40px', marginBottom: '32px' }}>
        {body ? (
          formatEmailBody(body)?.map((text, index) => (
            <EmailBodyText key={index}>{text}</EmailBodyText>
          ))
        ) : (
          <EmailBodyText>
            {username} from {brandName} has sent you a payment request. Details
            of the payment request can be found below. Click the Pay Now button
            to see further order details and make a payment. If you have any
            questions or concerns, please reach out to{' '}
            <a href={userEmail}>{userEmail}</a> directly.{' '}
          </EmailBodyText>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <PayNowButton disabled>Pay Now</PayNowButton>
      </div>
    </PreviewContainer>
  )
}

export default EmailPreview
